import * as React from 'react';

import {CrossBrowserHiddenFormSubmit} from 'shells/form/cross-browser-hidden-form-submit';
import {Label} from 'shells/form/label';

import type {Flow} from 'flow-to-typescript-codemod';
import type {
    CreateEmailAutomationInputFieldInput,
    DeleteEmailAutomationInputFieldInput,
    GetEmailAutomation_emailAutomation as Automation,
    MailChimpListBasic as MailChimpListBasicFragment,
    StageBasic as StageBasicFragment,
    UpdateEmailAutomationInputFieldInput,
} from 'nutshell-graphql-types';

import {GraphQLSelectMailChimp} from '../../../core/select/graphql-select-mailchimp';
import {SalesAutomationSidebarFormWrapper} from '../../../core/sidebar/sales-automation-sidebar-form-wrapper';

import {EXITS_STAGE_NEVER_MAGIC_ID, EmailAutomationSelect} from './email-automation-select';

import styles from '../sales-process-form.module.css';

type Props = {
    stagesetId: string;
    stageId: string;
    isFetchingLatestVersion: boolean;
    formSubmitBtnRef: (arg1?: HTMLInputElement | null | undefined) => any;
    emailAutomation: Automation | null | undefined;
    createEmailAutomation: (
        // We don't want to force the type value here, bacuase thats set in
        // form-emails.js. But, this is better than a flow ignore.
        arg1: Flow.Diff<
            CreateEmailAutomationInputFieldInput,
            {
                type: string;
            }
        >
    ) => Promise<any>;
    updateEmailAutomation: (arg1: UpdateEmailAutomationInputFieldInput) => Promise<any>;
    deleteEmailAutomation: (arg1: DeleteEmailAutomationInputFieldInput) => Promise<any>;
};

type State = {
    selectedKeepUntilStageId: string | null | undefined;
    selectedMailChimpListId: string | null | undefined;
};

export class FormMailchimpComponent extends React.PureComponent<Props, State> {
    // @ts-expect-error
    mailchimpListSelectFieldRef: HTMLInputElement;

    constructor(props: Props) {
        super(props);

        const {emailAutomation} = props;

        if (emailAutomation) {
            this.state = {
                selectedKeepUntilStageId: this.getExitsStageValueForState(
                    emailAutomation.exitsStage
                ),
                selectedMailChimpListId: emailAutomation.emailList
                    ? emailAutomation.emailList.id
                    : null,
            };
        } else {
            this.state = {
                selectedKeepUntilStageId: null,
                selectedMailChimpListId: null,
            };
        }
    }

    UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const {emailAutomation} = nextProps;

        if (emailAutomation && !this.props.emailAutomation) {
            this.setState({
                selectedKeepUntilStageId: this.getExitsStageValueForState(
                    emailAutomation.exitsStage
                ),
                selectedMailChimpListId: emailAutomation.emailList
                    ? emailAutomation.emailList.id
                    : null,
            });
        }
    }

    render() {
        return (
            <SalesAutomationSidebarFormWrapper
                id={this.props.emailAutomation ? this.props.emailAutomation.id : null}
                isFetchingLatestVersion={this.props.isFetchingLatestVersion}
                onSaveForm={this.handleSaveForm}
                onDelete={{
                    deleteConfirmationTitle:
                        'Are you sure you want to delete this emailAutomation?',
                    onDeleteConfirm: this.handleDeleteConfirm,
                }}
            >
                <h3 className={styles.subheader}>
                    When a lead enters this stage, add the lead to a MailChimp list to enter your
                    set of campaigns.
                </h3>
                <div style={{width: 250}} className={styles['form-input']}>
                    <Label htmlFor='mailchimp-list'>Add to MailChimp list</Label>
                    <GraphQLSelectMailChimp
                        // @ts-expect-error
                        inputId='mailchimp-list'
                        onClearValue={() => this.setState({selectedMailChimpListId: undefined})}
                        onChange={this.handleAddToListChange}
                        selectedId={this.state.selectedMailChimpListId}
                    />
                </div>
                <div style={{width: 250}} className={styles['form-input']}>
                    <Label htmlFor='exit-stage'>Remove from list after…</Label>
                    <EmailAutomationSelect
                        inputId='exit-stage'
                        stageId={this.props.stageId}
                        stagesetId={this.props.stagesetId}
                        onClearValue={() => this.setState({selectedKeepUntilStageId: undefined})}
                        onChange={this.handleKeepUntilChange}
                        selectedId={this.state.selectedKeepUntilStageId}
                        includeForever={true}
                    />
                </div>
                <CrossBrowserHiddenFormSubmit inputRef={this.props.formSubmitBtnRef} />
            </SalesAutomationSidebarFormWrapper>
        );
    }

    handleAddToListChange = (list: MailChimpListBasicFragment) => {
        this.setState({selectedMailChimpListId: list.id});
    };

    handleKeepUntilChange = (stage: StageBasicFragment) => {
        this.setState({selectedKeepUntilStageId: stage.id});
    };

    handleDeleteConfirm = (emailAutomationId: string) => {
        return this.props.deleteEmailAutomation({
            id: emailAutomationId,
        });
    };

    handleSaveForm = () => {
        if (
            this.props.emailAutomation &&
            this.state.selectedKeepUntilStageId &&
            this.state.selectedMailChimpListId
        ) {
            return this.props.updateEmailAutomation({
                id: this.props.emailAutomation.id,
                entersStageId: this.props.stageId,
                emailListId: this.state.selectedMailChimpListId,
                exitsStageId: this.sanitizeExitsStageValueBeforeSave(
                    this.state.selectedKeepUntilStageId
                ),
            });
        } else if (this.state.selectedKeepUntilStageId && this.state.selectedMailChimpListId) {
            return this.props.createEmailAutomation({
                entersStageId: this.props.stageId,
                emailListId: this.state.selectedMailChimpListId,
                exitsStageId: this.sanitizeExitsStageValueBeforeSave(
                    this.state.selectedKeepUntilStageId
                ),
            });
        }
    };

    /**
     * Takes our current state's "exits" id value, and translates that into
     * what should actually be passed to the server.
     *
     * Specifically, this means that if our currnet "exits" id value is the
     * magic string EXITS_STAGE_NEVER_MAGIC_ID, we'll pass `null`, since that's
     * how it's represented on the server
     *
     * @param  {string} value         Current id in our state
     * @return {?string}              Nullable id to pass to server
     */
    sanitizeExitsStageValueBeforeSave = (value: string) => {
        return value === EXITS_STAGE_NEVER_MAGIC_ID ? null : value;
    };

    /**
     * Translates a potentially null `exitsStage` value into the proper
     * id for us to seed our state with.
     *
     * If the exitsStage value passed is null, we know that our state should
     * be seeded with the magic "Forever - never remove from list" id.
     *
     * @param  {Object} [value]   - Nullable stage fragment
     * @return {string}           - stageId | magic "FOREVER" id
     */
    getExitsStageValueForState = (value?: StageBasicFragment | null) => {
        return value ? value.id : EXITS_STAGE_NEVER_MAGIC_ID;
    };
}
